import {HttpClient, HttpParams} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BackendConfiguration} from '@application/configuration/backend-configuration';
import {BmpDrawing} from '@domain/production-schedule/bmp-drawing';
import {CreelForProductionSchedule} from '@domain/production-schedule/creel-for-production-schedule';
import {Drawing} from '@domain/production-schedule/drawing';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {EpDrawing} from '@domain/production-schedule/ep-drawing';
import {MachineQualityForProductionSchedule} from '@domain/production-schedule/machine-quality-for-production-schedule';
import {MachineSummary} from '@domain/production-schedule/machine-summary';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleOrderLine} from '@domain/production-schedule/production-schedule-order-line';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {ProductionScheduleProductConfiguration} from '@domain/production-schedule/production-schedule-product-configuration';
import {ProductionScheduleStatus} from '@domain/production-schedule/production-schedule-status.enum';
import {CreelMap} from '@domain/textile-data/creel/creel-map';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {ZoneType} from '@domain/zone-type.enum';
import {ProductionSchedules} from '@infrastructure/http/production-schedule/production-schedules';
import {ParametersForPreselection} from '@presentation/pages/texfab/production-schedule/add/plan/parameters-for-preselection';
import {ConflictedOrderLine} from '@presentation/pages/texfab/production-schedule/confirm/conflicted-order-line';
import {Conflict, GridModel} from '@vdw/angular-component-library';
import {isEqual, map} from 'lodash-es';
import {Observable, of} from 'rxjs';
import {map as rxjsMap, switchMap, tap} from 'rxjs/operators';
import {getColoredYarnSetPaths} from '../helper/get-colored-yarn-sets-path';

@Injectable()
export class HttpProductionSchedulesService implements ProductionSchedules {
  private httpClient: HttpClient;
  private backendConfiguration: BackendConfiguration;

  public constructor(httpClient: HttpClient, backendConfiguration: BackendConfiguration) {
    this.httpClient = httpClient;
    this.backendConfiguration = backendConfiguration;
  }

  public isIdentifierAvailable(name: string): Observable<boolean> {
    const params = new HttpParams().set('name', name);

    return this.httpClient.get(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/check-name`, {params}).pipe(
      rxjsMap((response: {productionScheduleNameAvailable: boolean}) => {
        return response.productionScheduleNameAvailable;
      })
    );
  }

  public createProductionSchedule(
    machineSummary: MachineSummary,
    machineQuality: MachineQualityForProductionSchedule,
    practicalPickDensityInPicksPerMM: number,
    coloredYarnSets: OverviewListColoredYarnSetWithStartDent[],
    creel: CreelForProductionSchedule,
    runId: number,
    mappingForCurrentPositionOnMachine: CreelMap[],
    productConfiguration: ProductionScheduleProductConfiguration,
    isSample: boolean,
    canAddJute: boolean,
    hmiVersion: string,
    addToPlanboard: boolean,
    preselectionId: number
  ): Observable<ProductionSchedule> {
    return this.httpClient
      .post(`${this.backendConfiguration.getProductionScheduleEndpoint(2)}productionschedules/initial-create`, {
        machine: machineSummary.toJSON(),
        machineQuality: machineQuality.toJSON(),
        practicalPickDensityInPicksPerMM,
        coloredYarnSets: map(coloredYarnSets, (productionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent) => productionScheduleColoredYarnSet.toJSON()),
        creel: creel.toJSON(),
        runId,
        mappingForCurrentPositionOnMachine,
        productConfiguration: productConfiguration?.toJSON(),
        isSample,
        canAddJute,
        hmiVersion,
        addToPlanboard,
        preselectionId
      })
      .pipe(rxjsMap((productionScheduleJSON: any) => ProductionSchedule.fromJSON(productionScheduleJSON)));
  }

  public decreateProductionOrder(decreatedProductionOrder: ProductionSchedule): Observable<void> {
    return this.httpClient
      .post<void>(`${this.backendConfiguration.getProductionScheduleEndpoint(2)}productionschedules/initial-create`, decreatedProductionOrder.toJSON(true))
      .pipe(switchMap(() => this.saveDesignsAndOrderLines(decreatedProductionOrder.id, decreatedProductionOrder.designs, decreatedProductionOrder.orderLines, decreatedProductionOrder.name)));
  }

  public delete(id: number | string): Observable<void> {
    return this.httpClient.delete<void>(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/${id}`);
  }

  public getAll(): Observable<ProductionSchedule[]> {
    return of([]);
  }

  public getById(id: number | string): Observable<ProductionSchedule> {
    return this.httpClient
      .get(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/${id}`)
      .pipe(rxjsMap((productionScheduleJSON: any) => ProductionSchedule.fromJSON(productionScheduleJSON)));
  }

  public save(productionSchedule: ProductionSchedule): Observable<number> {
    return of();
  }

  public update(productionSchedule: ProductionSchedule): Observable<void> {
    return of();
  }

  public updateWithMachineQualityChanged(productionSchedule: ProductionSchedule, isMachineQualityChanged: boolean = false): Observable<void> {
    const query = isMachineQualityChanged ? '?updatePaths=true' : '';

    return this.httpClient.put<void>(
      `${this.backendConfiguration.getProductionScheduleEndpoint()}productionschedules/${productionSchedule.id}${query}`,
      productionSchedule.toJSON(false, isMachineQualityChanged)
    );
  }

  public getConflicts(id: number): Observable<Conflict[]> {
    return this.httpClient.get(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/${id}/conflicts`).pipe(
      rxjsMap((conflictsJSON: any) => {
        return map(conflictsJSON, (conflictJSON: any) => {
          return Conflict.fromJSON(conflictJSON);
        });
      })
    );
  }

  public setProductionSchedulePlan(
    productionScheduleId: string,
    productionSchedulePathsOfColoredYarnSets: ProductionSchedulePathsOfColoredYarnSet[],
    reedDensityInDentsPerMeter: number
  ): Observable<ProductionSchedule> {
    return this.httpClient
      .patch(`${this.backendConfiguration.getProductionScheduleEndpoint()}productionschedules/${productionScheduleId}`, {
        coloredYarnSetPaths: getColoredYarnSetPaths(productionSchedulePathsOfColoredYarnSets, reedDensityInDentsPerMeter)
      })
      .pipe(rxjsMap((productionSchedulePlanJSON: any) => ProductionSchedule.fromJSON(productionSchedulePlanJSON)));
  }

  public verifyProductionSchedule(id: string, manualSave: boolean = false, productionSchedulePhase: ProductionSchedulePhase = null): Observable<ProductionSchedule> {
    const query = manualSave ? `?manualSave=true&step=${productionSchedulePhase}` : `?manualSave=false&step=${productionSchedulePhase}`;

    return this.httpClient
      .get(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/${id}/verify${query}`)
      .pipe(rxjsMap((productionSchedulePlanJSON: any) => ProductionSchedule.fromJSON(productionSchedulePlanJSON)));
  }

  public cancelTemporarySavedChanges(id: string): Observable<void> {
    return this.httpClient.delete<void>(`${this.backendConfiguration.getProductionOrderEndpoint()}productionschedules/${id}`);
  }

  public checkVerifyProductionSchedule(productionScheduleId: string, type: ZoneType, startDent: number, value: number, coloredYarnSetPathNr: number): Observable<any> {
    return this.httpClient
      .post(`${this.backendConfiguration.getProductionScheduleEndpoint()}productionschedules/${productionScheduleId}/verify/check`, {
        type,
        startDent,
        value,
        coloredYarnSetPathNr
      })
      .pipe(rxjsMap((productionSchedulePlanJSON: any) => ProductionSchedule.fromJSON(productionSchedulePlanJSON)));
  }

  public completeAndPlan(productionSchedule: ProductionSchedule): Observable<void> {
    return this.httpClient.put<void>(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/${productionSchedule.id}/complete-and-plan`, productionSchedule.toJSON(true));
  }

  public completeAndQueue(productionSchedule: ProductionSchedule): Observable<void> {
    return this.httpClient.put<void>(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/${productionSchedule.id}/complete-and-queue`, productionSchedule.toJSON(true));
  }

  public process(productionSchedule: ProductionSchedule): Observable<void> {
    return this.httpClient.put<void>(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/${productionSchedule.id}/complete`, productionSchedule.toJSON(true));
  }

  public saveDesignsAndOrderLines(id: number | string, designs: Drawing[], orderLines: ProductionScheduleOrderLine[], name: string): Observable<void> {
    const designIds: number[] = designs.map((drawing: Drawing) => drawing.id);
    const orderLineIds: number[] = orderLines.map((orderLine: ProductionScheduleOrderLine) => orderLine.id);

    return this.httpClient.post<void>(`${this.backendConfiguration.getEndpoint()}productionschedules/${id}/designs-and-order-lines`, {designIds, orderLineIds, name});
  }

  public savePreselectionList(id: number | string, designIds: number[], orderLinesIds: number[], gridModel: GridModel, configurationIds: number[] = []): Observable<number> {
    return this.httpClient
      .post<void>(`${this.backendConfiguration.getProductionOrderEndpoint()}preselections`, {id, designIds, orderLinesIds, configurationIds, filter: gridModel})
      .pipe(rxjsMap((response: any) => response));
  }

  public updatePreselectionList(id: number | string, designIds: number[], orderLinesIds: number[], gridModel: GridModel, configurationIds: number[] = []): Observable<void> {
    return this.httpClient.put<void>(`${this.backendConfiguration.getProductionOrderEndpoint()}preselections`, {id, designIds, orderLinesIds, configurationIds, filter: gridModel});
  }

  public getDesignsAndOrderLines(id: number | string, status: ProductionScheduleStatus): Observable<{designs: Drawing[]; orderLines: ProductionScheduleOrderLine[]}> {
    return this.httpClient.get(`${this.backendConfiguration.getEndpoint()}productionschedules/${id}/designs-and-order-lines?status=${ProductionScheduleStatus[status]}`).pipe(
      rxjsMap((response: any) => {
        return {
          designs: map(response.designs, (designJSON: any) => {
            let result: Drawing;

            if (isEqual(DrawingType[designJSON.drawingType], DrawingType.BMP)) {
              result = BmpDrawing.fromJSON(designJSON);
            } else if (isEqual(DrawingType[designJSON.drawingType], DrawingType.EP)) {
              result = EpDrawing.fromJSON(designJSON);
            }

            return result;
          }),
          orderLines: map(response.orderLines, (orderLineJSON: any) => ProductionScheduleOrderLine.fromJSON(orderLineJSON))
        };
      })
    );
  }

  public getPreselectionListById(id: number | string): Observable<ParametersForPreselection> {
    return this.httpClient.get(`${this.backendConfiguration.getProductionOrderEndpoint()}preselections/${id}`).pipe(
      rxjsMap((response: any) => {
        return {
          designIds: response.designIds,
          configurationIds: response.configurationIds,
          orderlinesIds: response.orderlinesIds,
          gridModel: response.filter
        } as ParametersForPreselection;
      })
    );
  }

  public checkPossibleConflictsWithOrderLines(id: string): Observable<ConflictedOrderLine[]> {
    return this.httpClient.get<ConflictedOrderLine[]>(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/${id}/order-lines/amount-check`);
  }

  public getDuplicatedProductionOrder(id: number | string): Observable<ProductionSchedule> {
    return this.httpClient.get(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/${id}/duplicate`).pipe(
      rxjsMap((productionscheduleJSON: any) => {
        return ProductionSchedule.fromJSON(productionscheduleJSON);
      })
    );
  }

  public duplicateProductionOrder(productionOrderToDuplicate: ProductionSchedule): Observable<ProductionSchedule> {
    return this.httpClient.post<void>(`${this.backendConfiguration.getProductionScheduleEndpoint(2)}productionschedules/initial-create`, productionOrderToDuplicate.toJSON(true)).pipe(
      tap((productionOrderJSON: any) => {
        productionOrderToDuplicate.id = productionOrderJSON.id;
        productionOrderToDuplicate.status = ProductionScheduleStatus[productionOrderJSON.status];
      }),
      switchMap(() => this.saveDesignsAndOrderLines(productionOrderToDuplicate.id, productionOrderToDuplicate.designs, productionOrderToDuplicate.orderLines, productionOrderToDuplicate.name)),
      rxjsMap(() => productionOrderToDuplicate)
    );
  }

  public getGeneratedProductionOrderName(machineId: number, qualityId: number, creelId: number): Observable<string> {
    return this.httpClient
      .post(`${this.backendConfiguration.getAggregatorEndpoint()}productionschedules/generate-name?machineId=${machineId}&qualityId=${qualityId}&creelId=${creelId}`, null)
      .pipe(rxjsMap((response: string) => response));
  }
}
